import React, { useState, useRef, useEffect } from 'react';
import { PenTool, Plus, Trash2, Download, Loader2, FileText, Upload, X, Eye, CheckCircle, Send, MessageSquare, Sparkles, User, Briefcase, GraduationCap, Wrench, Mail, Phone, MapPin, Globe } from 'lucide-react';
import { cvAutofill, createCandidate } from '../services/api';
import { buildFlow, getActiveQuestions, getProgress, getCurrentSection, didSectionChange, isCvPhaseComplete } from '../data/cvFlowQuestions';
import './CvBuilderPage.css';

const emptyExperience = { company: '', title: '', startDate: '', endDate: '', description: '' };
const emptyEducation = { school: '', degree: '', startDate: '', endDate: '' };

function parseExperience(text) {
  const parts = text.split(/[-–—,]/).map(s => s.trim()).filter(Boolean);
  if (parts.length >= 2) {
    const datePart = parts.find(p => /\d{4}/.test(p));
    const dates = datePart ? datePart.match(/\d{4}/g) : [];
    const nonDateParts = parts.filter(p => p !== datePart);
    return {
      company: nonDateParts[0] || '',
      title: nonDateParts[1] || nonDateParts[0] || '',
      startDate: dates[0] || '',
      endDate: dates[1] || (datePart && /devam|halen|present/i.test(datePart) ? 'Devam' : ''),
      description: '',
    };
  }
  return { company: text, title: '', startDate: '', endDate: '', description: '' };
}

function parseEducation(text) {
  const parts = text.split(/[-–—,]/).map(s => s.trim()).filter(Boolean);
  if (parts.length >= 2) {
    const datePart = parts.find(p => /\d{4}/.test(p));
    const dates = datePart ? datePart.match(/\d{4}/g) : [];
    const nonDateParts = parts.filter(p => p !== datePart);
    return {
      school: nonDateParts[0] || '',
      degree: nonDateParts[1] || '',
      startDate: dates[0] || '',
      endDate: dates[1] || '',
    };
  }
  return { school: text, degree: '', startDate: '', endDate: '' };
}

const CvBuilderPage = () => {
  const [activeTab, setActiveTab] = useState('chat');
  const [cvList, setCvList] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Flow state
  const [flow] = useState(() => buildFlow());
  const [currentIdx, setCurrentIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatTyping, setChatTyping] = useState(false);
  const [chatFinished, setChatFinished] = useState(false);
  const [cvSaved, setCvSaved] = useState(false);
  const [lastSectionId, setLastSectionId] = useState(null);
  const chatEndRef = useRef(null);
  const [chatStarted, setChatStarted] = useState(false);

  // Experience/education accumulators
  const [experiences, setExperiences] = useState([]);
  const [educations, setEducations] = useState([]);

  // Upload in chat
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadParsed, setUploadParsed] = useState(null);
  const [uploadError, setUploadError] = useState('');
  const chatFileRef = useRef(null);

  // Manual form
  const [cvName, setCvName] = useState('');
  const [form, setForm] = useState({
    fullName: '', email: '', phone: '', location: '', summary: '',
    experiences: [{ ...emptyExperience }],
    educations: [{ ...emptyEducation }],
    skills: '',
  });

  const resetForm = () => {
    setForm({ fullName: '', email: '', phone: '', location: '', summary: '', experiences: [{ ...emptyExperience }], educations: [{ ...emptyEducation }], skills: '' });
    setCvName('');
  };

  const resetChat = () => {
    setCurrentIdx(0);
    setAnswers({});
    setChatMessages([]);
    setChatInput('');
    setChatTyping(false);
    setChatFinished(false);
    setCvSaved(false);
    setLastSectionId(null);
    setChatStarted(false);
    setExperiences([]);
    setEducations([]);
    setUploadFile(null);
    setUploadParsed(null);
    setUploadError('');
  };

  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const updateListItem = (list, index, field, value) => {
    setForm(prev => ({ ...prev, [list]: prev[list].map((item, i) => i === index ? { ...item, [field]: value } : item) }));
  };
  const addListItem = (list, empty) => setForm(prev => ({ ...prev, [list]: [...prev[list], { ...empty }] }));
  const removeListItem = (list, index) => setForm(prev => ({ ...prev, [list]: prev[list].filter((_, i) => i !== index) }));

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, chatTyping]);

  // Get active (visible) questions based on current answers
  const getActiveFlow = (currentAnswers) => {
    return getActiveQuestions(flow, currentAnswers);
  };

  // Find next question index in the active flow
  const findNextActiveIndex = (afterIdx, currentAnswers) => {
    const activeFlow = getActiveQuestions(flow, currentAnswers);
    const currentQ = flow[afterIdx];
    if (!currentQ) return -1;
    const activeIdx = activeFlow.findIndex(q => q.id === currentQ.id);
    if (activeIdx === -1 || activeIdx + 1 >= activeFlow.length) return -1;
    const nextQ = activeFlow[activeIdx + 1];
    return flow.findIndex(q => q.id === nextQ.id);
  };

  // Start chat flow
  const startChat = () => {
    if (chatStarted) return;
    setChatStarted(true);
    const firstQ = flow[0];
    if (!firstQ) return;
    const section = getCurrentSection(firstQ.id);
    const msgs = [];
    if (section) {
      msgs.push({ role: 'section', text: section.title, sectionId: section.id });
      setLastSectionId(section.id);
    }
    msgs.push({ role: 'ai', text: firstQ.text, questionId: firstQ.id, type: firstQ.type, choices: firstQ.choices });
    setChatMessages(msgs);
  };

  // Auto-start chat when tab is active
  useEffect(() => {
    if (activeTab === 'chat' && !chatStarted && flow.length > 0) {
      startChat();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Add section divider if needed
  const maybeSectionDivider = (prevQId, nextQId) => {
    if (didSectionChange(prevQId, nextQId)) {
      const section = getCurrentSection(nextQId);
      if (section && section.id !== lastSectionId) {
        setLastSectionId(section.id);
        return { role: 'section', text: section.title, sectionId: section.id };
      }
    }
    return null;
  };

  // Advance to next question
  const advanceToNext = (fromIdx, newAnswers) => {
    // If user said no to abroad, we're done
    if (newAnswers.abroadInterest === 'no') {
      setChatFinished(true);
      setTimeout(() => {
        setChatMessages(prev => [...prev, {
          role: 'ai',
          text: 'Tamam! CV\'niz hazır. Aşağıdan kaydedebilirsiniz. İstediğiniz zaman geri dönüp yurt dışı modülünü tamamlayabilirsiniz.',
          questionId: '__done',
        }]);
        setChatTyping(false);
      }, 600);
      return;
    }

    const nextIdx = findNextActiveIndex(fromIdx, newAnswers);
    if (nextIdx === -1 || nextIdx >= flow.length) {
      // All questions done
      setChatFinished(true);
      setTimeout(() => {
        setChatMessages(prev => [...prev, {
          role: 'ai',
          text: 'Tebrikler! Tüm bilgileriniz alındı. CV\'niz ve kariyer profiliniz hazır. Aşağıdan kaydedebilirsiniz.',
          questionId: '__done',
        }]);
        setChatTyping(false);
      }, 600);
      return;
    }

    const nextQ = flow[nextIdx];
    setCurrentIdx(nextIdx);

    setTimeout(() => {
      const newMsgs = [];
      const divider = maybeSectionDivider(flow[fromIdx]?.id, nextQ.id);
      if (divider) newMsgs.push(divider);
      newMsgs.push({ role: 'ai', text: nextQ.text, questionId: nextQ.id, type: nextQ.type, choices: nextQ.choices });
      setChatMessages(prev => [...prev, ...newMsgs]);
      setChatTyping(false);
    }, 600);
  };

  // Validation rules per field
  const validateField = (fieldId, value) => {
    switch (fieldId) {
      case 'fullName': {
        const cleaned = value.replace(/[^a-zA-ZçÇğĞıİöÖşŞüÜâÂîÎûÛ\s]/g, '');
        if (cleaned.trim().split(/\s+/).length < 2) return 'Lütfen adınızı ve soyadınızı yazın. (Örn: Ahmet Yılmaz)';
        return null;
      }
      case 'phone': {
        const digits = value.replace(/[^0-9+]/g, '');
        if (digits.length < 10) return 'Telefon numarası çok kısa. Lütfen alan koduyla birlikte yazın. (Örn: +90 532 123 4567)';
        if (!/^\+?\d{10,15}$/.test(digits)) return 'Geçerli bir telefon numarası girin. Uluslararası alan koduyla başlamalı. (Örn: +90 532 123 4567)';
        return null;
      }
      case 'email': {
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Geçerli bir e-posta adresi girin. (Örn: isim@email.com)';
        return null;
      }
      case 'birthDate': {
        const dateMatch = value.match(/(\d{1,2})[./\-](\d{1,2})[./\-](\d{4})/);
        if (!dateMatch) return 'Doğum tarihinizi Gün/Ay/Yıl formatında yazın. (Örn: 15/03/1990)';
        const [, d, m, y] = dateMatch.map(Number);
        if (m < 1 || m > 12 || d < 1 || d > 31) return 'Geçerli bir tarih girin. (Örn: 15/03/1990)';
        const age = new Date().getFullYear() - y;
        if (age < 16 || age > 80) return 'Doğum yılını kontrol edin. Yaşınız 16-80 arasında olmalıdır.';
        return null;
      }
      default:
        return null;
    }
  };

  // Handle user answer (text or choice)
  const handleAnswer = (text, choiceValue) => {
    const currentQ = flow[currentIdx];
    if (!currentQ) return;

    const displayText = choiceValue
      ? currentQ.choices?.find(c => c.value === choiceValue)?.label || text
      : text;

    // Validate before accepting (skip for choice questions)
    if (!choiceValue) {
      const error = validateField(currentQ.field, text.trim());
      if (error) {
        setChatMessages(prev => [...prev,
          { role: 'user', text: displayText },
          { role: 'ai', text: error, questionId: currentQ.id, type: currentQ.type, choices: currentQ.choices },
        ]);
        return;
      }
    }

    setChatMessages(prev => [...prev, { role: 'user', text: displayText }]);
    setChatTyping(true);

    const value = choiceValue || text.trim();
    const newAnswers = { ...answers, [currentQ.field]: value };

    // Accumulate experiences/educations
    if (currentQ.id === 'experience_1') {
      const parsed = parseExperience(text);
      setExperiences(prev => [...prev, parsed]);
    }
    if (currentQ.id === 'education_1') {
      const parsed = parseEducation(text);
      setEducations(prev => [...prev, parsed]);
    }

    setAnswers(newAnswers);

    // Handle loop (experience_more)
    if (currentQ.loop && value === currentQ.loop.onValue) {
      const jumpToIdx = flow.findIndex(q => q.id === currentQ.loop.jumpTo);
      if (jumpToIdx !== -1) {
        setCurrentIdx(jumpToIdx);
        const jumpQ = flow[jumpToIdx];
        setTimeout(() => {
          setChatMessages(prev => [...prev, {
            role: 'ai', text: jumpQ.text, questionId: jumpQ.id, type: jumpQ.type, choices: jumpQ.choices,
          }]);
          setChatTyping(false);
        }, 600);
        return;
      }
    }

    advanceToNext(currentIdx, newAnswers);
  };

  const handleChatSend = () => {
    const text = chatInput.trim();
    if (!text || chatTyping || chatFinished) return;
    setChatInput('');
    handleAnswer(text, null);
  };

  const handleChoiceClick = (choiceValue) => {
    if (chatTyping || chatFinished) return;
    handleAnswer(choiceValue, choiceValue);
  };

  const handleChatKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleChatSend(); }
  };

  const handleChatSave = async () => {
    setGenerating(true);
    try {
      await new Promise(r => setTimeout(r, 1500));
      const newCv = {
        id: Date.now(),
        name: answers.cvName || `${answers.fullName || 'CV'} - ${new Date().toLocaleDateString('tr-TR')}`,
        title: experiences[0]?.title || '',
        location: answers.location,
        createdAt: new Date().toISOString(),
        source: 'chat',
      };
      setCvList(prev => [newCv, ...prev]);
      setCvSaved(true);
    } catch {
      alert('CV kaydedilemedi.');
    } finally {
      setGenerating(false);
    }
  };

  // Upload within chat tab
  const handleUploadParse = async () => {
    if (!uploadFile) return;
    setUploading(true);
    setUploadError('');
    setUploadParsed(null);
    try {
      const data = await cvAutofill(uploadFile);
      setUploadParsed(data);
      const newAnswers = { ...answers };
      if (data.fullName || data.name) newAnswers.fullName = data.fullName || data.name;
      if (data.email) newAnswers.email = data.email;
      if (data.phone) newAnswers.phone = data.phone;
      if (data.currentLocation) newAnswers.location = data.currentLocation;
      if (data.summary) newAnswers.summary = data.summary;
      if (data.skills) newAnswers.skills = Array.isArray(data.skills) ? data.skills.join(', ') : data.skills;
      setAnswers(newAnswers);
      setChatMessages(prev => [...prev,
        { role: 'ai', text: 'CV dosyanız analiz edildi! Bilgileriniz aşağıdaki önizlemeye yansıtıldı. Eksik bilgileri yazışarak tamamlayabilirsiniz.' }
      ]);
    } catch {
      setUploadError('CV analiz edilemedi. Lütfen tekrar deneyin.');
    } finally {
      setUploading(false);
    }
  };

  const handleUploadSave = async () => {
    if (!uploadParsed) return;
    setUploading(true);
    setUploadError('');
    try {
      const payload = {
        name: uploadFile?.name?.replace(/\.[^.]+$/, '') || 'CV',
        title: uploadParsed.title || '',
        currentLocation: uploadParsed.currentLocation || '',
        experienceYear: uploadParsed.experienceYear ?? null,
        skills: Array.isArray(uploadParsed.skills) ? uploadParsed.skills.join(', ') : (uploadParsed.skills || ''),
        summary: uploadParsed.summary || '',
        education: uploadParsed.education || '',
        educationLevelId: uploadParsed.educationLevelId ?? null,
        age: uploadParsed.age ?? null,
        isMillitaryCompleted: uploadParsed.isMillitaryCompleted ?? null,
        source: 'upload',
      };
      const result = await createCandidate(payload);
      const savedCv = result.data || result;
      setCvList(prev => [{
        id: savedCv.id || Date.now(),
        name: payload.name,
        title: payload.title,
        location: payload.currentLocation,
        createdAt: savedCv.createdAt || new Date().toISOString(),
        source: 'upload',
      }, ...prev]);
      setUploadFile(null);
      setUploadParsed(null);
      setUploadError('');
      if (chatFileRef.current) chatFileRef.current.value = '';
    } catch {
      setUploadError('CV kaydedilemedi. Lütfen tekrar deneyin.');
    } finally {
      setUploading(false);
    }
  };

  // Manual form
  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await new Promise(r => setTimeout(r, 2000));
      const newCv = {
        id: Date.now(),
        name: cvName || `${form.fullName || 'CV'} - ${new Date().toLocaleDateString('tr-TR')}`,
        title: form.experiences[0]?.title || '',
        location: form.location,
        createdAt: new Date().toISOString(),
        source: 'manual',
      };
      setCvList(prev => [newCv, ...prev]);
      resetForm();
    } catch {
      alert('CV oluşturulamadı.');
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = (id) => setCvList(prev => prev.filter(cv => cv.id !== id));

  const formatDate = (dateStr) => {
    try { return new Date(dateStr).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' }); }
    catch { return dateStr; }
  };

  const hasCvContent = answers.fullName || experiences.length > 0 || educations.length > 0 || answers.skills;

  // CV is saveable once cv_name is answered
  const cvSaveable = isCvPhaseComplete(answers);

  // Progress calculation
  const activeFlow = chatStarted ? getActiveFlow(answers) : [];
  const currentActiveIdx = activeFlow.findIndex(q => q.id === flow[currentIdx]?.id);
  const progress = getProgress(currentActiveIdx >= 0 ? currentActiveIdx : 0, activeFlow.length);

  // Current question for choice rendering
  const currentQuestion = flow[currentIdx];
  const isChoiceQuestion = currentQuestion?.type === 'choice' && !chatFinished && !chatTyping;

  return (
    <div className="cv-builder-page">
      <div className="cv-builder__container">

        <div className="cv-builder__header">
          <PenTool size={24} />
          <div>
            <h1 className="cv-builder__title">CV'lerim</h1>
            <p className="cv-builder__subtitle">Yapay zeka ile yazışarak veya manuel olarak CV oluştur.</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="cv-builder__tabs">
          <button className={`cv-builder__tab ${activeTab === 'chat' ? 'cv-builder__tab--active' : ''}`} onClick={() => setActiveTab('chat')}>
            <MessageSquare size={16} /> Akıllı CV Asistanı
          </button>
          <button className={`cv-builder__tab ${activeTab === 'manual' ? 'cv-builder__tab--active' : ''}`} onClick={() => setActiveTab('manual')}>
            <PenTool size={16} /> Manuel Oluştur
          </button>
          <button className={`cv-builder__tab ${activeTab === 'list' ? 'cv-builder__tab--active' : ''}`} onClick={() => setActiveTab('list')}>
            <FileText size={16} /> CV'lerim {cvList.length > 0 && <span className="cv-builder__tab-badge">{cvList.length}</span>}
          </button>
        </div>

        {/* ── TAB: Chat ── */}
        {activeTab === 'chat' && (
          <>
                {/* Progress Bar */}
                <div className="cv-progress">
                  <div className="cv-progress__bar">
                    <div className="cv-progress__fill" style={{ width: `${chatFinished ? 100 : progress}%` }} />
                  </div>
                  <div className="cv-progress__info">
                    <span className="cv-progress__label">
                      {chatFinished ? 'Tamamlandı' : getCurrentSection(flow[currentIdx]?.id)?.title || ''}
                    </span>
                    <span className="cv-progress__pct">{chatFinished ? 100 : progress}%</span>
                  </div>
                </div>

                {/* Chat */}
                <div className="cv-chat">
                  <div className="cv-chat__messages">
                    {chatMessages.map((msg, i) => {
                      if (msg.role === 'section') {
                        return (
                          <div key={i} className="cv-chat__section-divider">
                            <div className="cv-chat__section-line" />
                            <span className="cv-chat__section-badge">{msg.text}</span>
                            <div className="cv-chat__section-line" />
                          </div>
                        );
                      }
                      return (
                        <div key={i} className={`cv-chat__msg cv-chat__msg--${msg.role}`}>
                          <div className="cv-chat__msg-avatar">
                            {msg.role === 'ai' ? <Sparkles size={14} /> : <User size={14} />}
                          </div>
                          <div className="cv-chat__msg-bubble">
                            {msg.text.split('\n').map((line, li) => (
                              <React.Fragment key={li}>{line}{li < msg.text.split('\n').length - 1 && <br />}</React.Fragment>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                    {chatTyping && (
                      <div className="cv-chat__msg cv-chat__msg--ai">
                        <div className="cv-chat__msg-avatar"><Sparkles size={14} /></div>
                        <div className="cv-chat__msg-bubble">
                          <div className="cv-chat__typing"><span></span><span></span><span></span></div>
                        </div>
                      </div>
                    )}
                    <div ref={chatEndRef} />
                  </div>

                  {/* Choice buttons */}
                  {isChoiceQuestion && currentQuestion.choices && (
                    <div className="cv-chat__choices">
                      {currentQuestion.choices.map((choice) => (
                        <button
                          key={choice.value}
                          className="cv-chat__choice-btn"
                          onClick={() => handleChoiceClick(choice.value)}
                        >
                          {choice.label}
                        </button>
                      ))}
                    </div>
                  )}

                  <div className="cv-chat__input-area">
                    <div className="cv-chat__input-wrap">
                      <input
                        ref={chatFileRef}
                        type="file"
                        accept=".pdf,.doc,.docx"
                        className="cv-builder__file-input"
                        id="chat-cv-upload"
                        onChange={(e) => { if (e.target.files[0]) { setUploadFile(e.target.files[0]); setUploadParsed(null); setUploadError(''); } }}
                      />
                      <label htmlFor="chat-cv-upload" className="cv-chat__upload-btn" title="CV Yükle">
                        <Upload size={16} />
                      </label>

                      <input
                        className="cv-chat__input"
                        placeholder={chatFinished ? 'CV\'niz hazır!' : isChoiceQuestion ? 'Yukarıdan bir seçenek seçin veya yazarak cevaplayın...' : 'Cevabınızı yazın...'}
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value)}
                        onKeyDown={handleChatKeyDown}
                        disabled={chatTyping || chatFinished}
                      />
                      <button className="cv-chat__send" onClick={handleChatSend} disabled={!chatInput.trim() || chatTyping || chatFinished}>
                        <Send size={16} />
                      </button>
                    </div>

                    {/* Upload file badge */}
                    {uploadFile && !uploadParsed && (
                      <div className="cv-chat__upload-status">
                        <div className="cv-builder__file-badge">
                          <FileText size={14} />
                          <span>{uploadFile.name}</span>
                          <button onClick={() => { setUploadFile(null); setUploadError(''); if (chatFileRef.current) chatFileRef.current.value = ''; }}>
                            <X size={12} />
                          </button>
                        </div>
                        <button className="cv-chat__analyze-btn" onClick={handleUploadParse} disabled={uploading}>
                          {uploading ? <><Loader2 size={14} className="cv-builder__spinner" /> Analiz ediliyor...</> : <><Sparkles size={14} /> CV Analiz Et</>}
                        </button>
                      </div>
                    )}

                    {uploadParsed && (
                      <div className="cv-chat__upload-status">
                        <div className="cv-chat__upload-success">
                          <CheckCircle size={14} /> CV analiz edildi ve önizlemeye yansıtıldı
                        </div>
                        <button className="cv-chat__analyze-btn" onClick={handleUploadSave} disabled={uploading}>
                          {uploading ? <><Loader2 size={14} className="cv-builder__spinner" /> Kaydediliyor...</> : <><Download size={14} /> CV Olarak Kaydet</>}
                        </button>
                      </div>
                    )}

                    {uploadError && <p className="cv-builder__error" style={{ marginTop: 8 }}>{uploadError}</p>}

                    {/* Reset button */}
                    <button className="cv-chat__reset" onClick={resetChat}>
                      Baştan Başla
                    </button>
                  </div>
                </div>

                {/* CV Preview */}
                {hasCvContent && (
                  <div className="cv-preview">
                    <div className="cv-preview__header-bar">
                      <FileText size={16} />
                      <span>CV Önizleme</span>
                    </div>
                    <div className="cv-preview__paper">
                      {answers.fullName && (
                        <div className="cv-preview__personal">
                          <h2 className="cv-preview__name">{answers.fullName}</h2>
                          <div className="cv-preview__contact">
                            {answers.email && <span><Mail size={12} /> {answers.email}</span>}
                            {answers.phone && <span><Phone size={12} /> {answers.phone}</span>}
                            {answers.location && <span><MapPin size={12} /> {answers.location}</span>}
                          </div>
                        </div>
                      )}

                      {answers.summary && (
                        <div className="cv-preview__section">
                          <h3 className="cv-preview__section-title"><User size={14} /> Profesyonel Özet</h3>
                          <p className="cv-preview__text">{answers.summary}</p>
                        </div>
                      )}

                      {experiences.length > 0 && (
                        <div className="cv-preview__section">
                          <h3 className="cv-preview__section-title"><Briefcase size={14} /> İş Deneyimi</h3>
                          {experiences.map((exp, i) => (
                            <div key={i} className="cv-preview__item">
                              <div className="cv-preview__item-header">
                                <strong>{exp.title || exp.company}</strong>
                                {exp.startDate && <span className="cv-preview__date">{exp.startDate}{exp.endDate ? ` - ${exp.endDate}` : ''}</span>}
                              </div>
                              {exp.company && exp.title && <p className="cv-preview__sub">{exp.company}</p>}
                              {exp.description && <p className="cv-preview__text">{exp.description}</p>}
                            </div>
                          ))}
                          {answers.experienceDetail && (
                            <p className="cv-preview__text" style={{ marginTop: 8 }}>{answers.experienceDetail}</p>
                          )}
                        </div>
                      )}

                      {educations.length > 0 && (
                        <div className="cv-preview__section">
                          <h3 className="cv-preview__section-title"><GraduationCap size={14} /> Eğitim</h3>
                          {educations.map((edu, i) => (
                            <div key={i} className="cv-preview__item">
                              <div className="cv-preview__item-header">
                                <strong>{edu.school}</strong>
                                {edu.startDate && <span className="cv-preview__date">{edu.startDate}{edu.endDate ? ` - ${edu.endDate}` : ''}</span>}
                              </div>
                              {edu.degree && <p className="cv-preview__sub">{edu.degree}</p>}
                            </div>
                          ))}
                          {answers.vocationalCert && (
                            <p className="cv-preview__text" style={{ marginTop: 8 }}>Mesleki Belgeler: {answers.vocationalCert}</p>
                          )}
                        </div>
                      )}

                      {answers.skills && (
                        <div className="cv-preview__section">
                          <h3 className="cv-preview__section-title"><Wrench size={14} /> Yetkinlikler</h3>
                          <div className="cv-preview__skills">
                            {answers.skills.split(/[,،;]+/).map(s => s.trim()).filter(Boolean).map((skill, i) => (
                              <span key={i} className="cv-preview__skill-tag">{skill}</span>
                            ))}
                          </div>
                        </div>
                      )}

                      {(answers.certificates || answers.driverLicense) && (
                        <div className="cv-preview__section">
                          <h3 className="cv-preview__section-title"><CheckCircle size={14} /> Sertifikalar ve Lisanslar</h3>
                          {answers.certificates && <p className="cv-preview__text">{answers.certificates}</p>}
                          {answers.driverLicense && <p className="cv-preview__text">Ehliyet: {answers.driverLicense}</p>}
                        </div>
                      )}

                      {answers.englishLevel && (
                        <div className="cv-preview__section">
                          <h3 className="cv-preview__section-title"><Globe size={14} /> Dil Bilgisi</h3>
                          <p className="cv-preview__text">
                            İngilizce: {
                              { none: 'Bilmiyor', basic: 'Temel (A1-A2)', intermediate: 'Orta (B1-B2)', advanced: 'İleri (C1-C2)', fluent: 'Akıcı' }[answers.englishLevel] || answers.englishLevel
                            }
                            {answers.englishExam && ` | ${answers.englishExam}`}
                          </p>
                          {answers.otherLanguages && <p className="cv-preview__text">Diğer: {answers.otherLanguages}</p>}
                        </div>
                      )}
                    </div>

                    {(cvSaveable && !cvSaved) && (
                      <button className="cv-builder__generate" onClick={handleChatSave} disabled={generating}>
                        {generating ? <><Loader2 size={18} className="cv-builder__spinner" /> Kaydediliyor...</> : <><CheckCircle size={18} /> CV Kaydet</>}
                      </button>
                    )}
                    {cvSaved && (
                      <p className="cv-preview__saved-badge"><CheckCircle size={14} /> CV kaydedildi</p>
                    )}
                  </div>
                )}
          </>
        )}

        {/* ── TAB: Manual ── */}
        {activeTab === 'manual' && (
          <div className="cv-builder__form">
            <div className="cv-builder__section">
              <div className="cv-builder__field">
                <label className="cv-builder__label">CV Adı</label>
                <input className="cv-builder__input" placeholder="Örn: Garson CV, Genel CV" value={cvName} onChange={(e) => setCvName(e.target.value)} />
              </div>
            </div>

            <div className="cv-builder__section">
              <h2 className="cv-builder__section-title">Kişisel Bilgiler</h2>
              <div className="cv-builder__row">
                <input className="cv-builder__input" placeholder="Ad Soyad" value={form.fullName} onChange={(e) => updateField('fullName', e.target.value)} />
                <input className="cv-builder__input" placeholder="E-posta" type="email" value={form.email} onChange={(e) => updateField('email', e.target.value)} />
              </div>
              <div className="cv-builder__row">
                <input className="cv-builder__input" placeholder="Telefon" value={form.phone} onChange={(e) => updateField('phone', e.target.value)} />
                <input className="cv-builder__input" placeholder="Konum" value={form.location} onChange={(e) => updateField('location', e.target.value)} />
              </div>
              <textarea className="cv-builder__textarea" placeholder="Profesyonel özet..." rows={3} value={form.summary} onChange={(e) => updateField('summary', e.target.value)} />
            </div>

            <div className="cv-builder__section">
              <div className="cv-builder__section-header">
                <h2 className="cv-builder__section-title">İş Deneyimi</h2>
                <button className="cv-builder__add-btn" onClick={() => addListItem('experiences', emptyExperience)}>
                  <Plus size={14} /> Ekle
                </button>
              </div>
              {form.experiences.map((exp, i) => (
                <div key={i} className="cv-builder__list-item">
                  <div className="cv-builder__row">
                    <input className="cv-builder__input" placeholder="Şirket" value={exp.company} onChange={(e) => updateListItem('experiences', i, 'company', e.target.value)} />
                    <input className="cv-builder__input" placeholder="Pozisyon" value={exp.title} onChange={(e) => updateListItem('experiences', i, 'title', e.target.value)} />
                  </div>
                  <div className="cv-builder__row">
                    <input className="cv-builder__input" placeholder="Başlangıç (2020)" value={exp.startDate} onChange={(e) => updateListItem('experiences', i, 'startDate', e.target.value)} />
                    <input className="cv-builder__input" placeholder="Bitiş (2024 veya Devam)" value={exp.endDate} onChange={(e) => updateListItem('experiences', i, 'endDate', e.target.value)} />
                  </div>
                  <textarea className="cv-builder__textarea" placeholder="Açıklama..." rows={2} value={exp.description} onChange={(e) => updateListItem('experiences', i, 'description', e.target.value)} />
                  {form.experiences.length > 1 && (
                    <button className="cv-builder__remove-btn" onClick={() => removeListItem('experiences', i)}>
                      <Trash2 size={14} /> Kaldır
                    </button>
                  )}
                </div>
              ))}
            </div>

            <div className="cv-builder__section">
              <div className="cv-builder__section-header">
                <h2 className="cv-builder__section-title">Eğitim</h2>
                <button className="cv-builder__add-btn" onClick={() => addListItem('educations', emptyEducation)}>
                  <Plus size={14} /> Ekle
                </button>
              </div>
              {form.educations.map((edu, i) => (
                <div key={i} className="cv-builder__list-item">
                  <div className="cv-builder__row">
                    <input className="cv-builder__input" placeholder="Okul" value={edu.school} onChange={(e) => updateListItem('educations', i, 'school', e.target.value)} />
                    <input className="cv-builder__input" placeholder="Bölüm / Derece" value={edu.degree} onChange={(e) => updateListItem('educations', i, 'degree', e.target.value)} />
                  </div>
                  <div className="cv-builder__row">
                    <input className="cv-builder__input" placeholder="Başlangıç" value={edu.startDate} onChange={(e) => updateListItem('educations', i, 'startDate', e.target.value)} />
                    <input className="cv-builder__input" placeholder="Bitiş" value={edu.endDate} onChange={(e) => updateListItem('educations', i, 'endDate', e.target.value)} />
                  </div>
                  {form.educations.length > 1 && (
                    <button className="cv-builder__remove-btn" onClick={() => removeListItem('educations', i)}>
                      <Trash2 size={14} /> Kaldır
                    </button>
                  )}
                </div>
              ))}
            </div>

            <div className="cv-builder__section">
              <h2 className="cv-builder__section-title">Yetkinlikler</h2>
              <input className="cv-builder__input" placeholder="React, Node.js, Proje Yönetimi... (virgülle ayır)" value={form.skills} onChange={(e) => updateField('skills', e.target.value)} />
            </div>

            <button className="cv-builder__generate" onClick={handleGenerate} disabled={generating}>
              {generating ? <><Loader2 size={18} className="cv-builder__spinner" /> Oluşturuluyor...</> : <><Download size={18} /> CV Oluştur</>}
            </button>
          </div>
        )}

        {/* ── TAB: List ── */}
        {activeTab === 'list' && (
          <>
            {cvList.length === 0 ? (
              <div className="cv-builder__empty">
                <FileText size={40} />
                <h3>Henüz CV'n yok</h3>
                <p>Yapay zeka ile yazışarak veya manuel olarak CV oluşturabilirsin.</p>
              </div>
            ) : (
              <div className="cv-builder__grid">
                {cvList.map(cv => (
                  <div key={cv.id} className="cv-builder__cv-card">
                    <div className="cv-builder__cv-icon">
                      <FileText size={20} />
                    </div>
                    <div className="cv-builder__cv-info">
                      <h3 className="cv-builder__cv-name">{cv.name}</h3>
                      <div className="cv-builder__cv-meta">
                        {cv.title && <span>{cv.title}</span>}
                        {cv.location && <span>{cv.location}</span>}
                        <span>{formatDate(cv.createdAt)}</span>
                      </div>
                      <span className={`cv-builder__cv-source cv-builder__cv-source--${cv.source}`}>
                        {cv.source === 'upload' ? 'Yüklenen' : cv.source === 'chat' ? 'Yapay Zeka' : 'Manuel'}
                      </span>
                    </div>
                    <div className="cv-builder__cv-actions">
                      <button className="cv-builder__cv-action" title="Görüntüle"><Eye size={14} /></button>
                      <button className="cv-builder__cv-action" title="İndir"><Download size={14} /></button>
                      <button className="cv-builder__cv-action cv-builder__cv-action--danger" title="Sil" onClick={() => handleDelete(cv.id)}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

      </div>
    </div>
  );
};

export default CvBuilderPage;
